import { Co2MaterialSettingType, FeatureManagementType, ProfileLevelType, ProfileResponse, ProjectMetadata, ProjectResponse } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useProfileDetailsById, useProfileSearch } from "@api/profiles";
import { useExportProfile, downloadBlobAsFile } from "@api/profileExport";
import { useUpdateProjectMetadata, useUpdateProjectProfile } from "@api/projects";
import ProfileDetailPanel, { ProfileResponseModel } from "@features/settings/components/ProfilePage/ProfileDetailPanel";
import ProfileImportDisplay from "@features/settings/components/ProfilePage/ProfileImportDisplay";
import MaterialsPage from "@features/settings/components/MaterialsPage/MaterialsPage";
import RebarEstimationPage from "@features/settings/components/RebarEstimationPage/RebarEstimationPage";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import DownloadIcon from "@mui/icons-material/Download";
import { UploadFile } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Select, SelectChangeEvent, Snackbar } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ProjectMetadataForm } from "../../../dashboard/components/ProjectMetadataForm";

interface ProjectSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectResponse | undefined;
  projectId: string;
}

export const ProjectSettingsDialog: React.FC<ProjectSettingsDialogProps> = ({ open, onClose, project, projectId }) => {
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();
  const appSettings = useAppSettings();
  const appStore = useAppStore();
  const updateProjectMetadata = useUpdateProjectMetadata(projectId);

  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const selectionManager = useSelectionManager();
  const updateProjectProfile = useUpdateProjectProfile(projectId);
  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileError,
    error: profileErrorDetails,
    refetch: refetchProfiles,
  } = useProfileSearch([ProfileLevelType.Application, ProfileLevelType.ApplicationImport], projectId);

  const [profileOptions, setProfileOptions] = useState<{ label: string; value: string; profileLevelType?: string }[]>([]);

  // Debug logging for useProfileSearch
  useEffect(() => {
    console.log("useProfileSearch Debug:", {
      projectId,
      profileData,
      profileLoading,
      profileError,
      profileErrorDetails,
      profileDataLength: profileData?.length,
    });
  }, [projectId, profileData, profileLoading, profileError, profileErrorDetails]);

  // State for metadata form
  const [metadata, setMetadata] = useState<ProjectMetadata>(
    new ProjectMetadata({
      totalFloorArea: undefined,
      serviceLife: undefined,
      goal: "",
      scope: "",
    }),
  );

  // Track if metadata has changed
  const [hasMetadataChanged, setHasMetadataChanged] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showProfileSuccessMessage, setshowProfileSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [importFiles, setImportFiles] = useState<File[] | undefined>(undefined);
  const [isImportMode, setIsImportMode] = useState(false);
  const [hasProfileChanged, setHasProfileChanged] = useState(false);
  const [exportToast, setExportToast] = useState<{ open: boolean; success: boolean }>({ open: false, success: false });

  const materialMode = appSettings.data?.profileLevelRebarSettings ? Co2MaterialSettingType.Profile : Co2MaterialSettingType.Project;
  const isProfileMode = materialMode === Co2MaterialSettingType.Profile;

  const exportProfile = useExportProfile({
    onSuccess: (blob, variables) => {
      const profileName = profileOptions.find((p) => p.value === variables.profileId)?.label || "profile";
      downloadBlobAsFile(blob, `${profileName}_export.xlsx`);
      setExportToast({ open: true, success: true });
    },
    onError: () => {
      setExportToast({ open: true, success: false });
    },
  });

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    const filesCopy = [...(event.target.files as FileList)];
    setImportFiles(filesCopy);
    setIsImportMode(true);

    if (file) {
      // Check if it's an Excel file
      const isExcelFile =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");

      if (isExcelFile) {
        setSelectedFile(file);
        console.info("Excel file selected:", file.name);
        // Here you can add logic to process the Excel file
      } else {
        setErrorMessage("Please select a valid Excel file (.xlsx or .xls)");
        setShowErrorMessage(true);
      }
    }

    event.target.value = "";
  };

  const handleSaveProfile = async () => {
    try {
      await updateProjectProfile.mutateAsync(selectedProfileValue);
      setHasProfileChanged(false);
      setshowProfileSuccessMessage(true);
    } catch (error) {
      console.info("Error updating project profile:", error);
      console.error("Error updating project profile:", error);
      setShowErrorMessage(true);
      setErrorMessage("Failed to update project profile. Please try again.");
      setshowProfileSuccessMessage(false);
    }
  };

  const handleExportProfile = async () => {
    if (!selectedProfileValue) return;
    try {
      await exportProfile.mutateAsync({ profileId: selectedProfileValue });
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  useEffect(() => {
    if (profileData && profileData.length > 0) {
      console.info("Processing profile data:", profileData);
      const filteredProfiles = profileData;
      const _profileOptions = filteredProfiles.map((profile) => ({
        label: profile.name ?? "",
        value: profile.profileId ?? "",
        profileLevelType: profile.profileLevelType,
      }));

      setSelectedProfileValue(project?.project?.profileId ?? "");
      setActiveProfileId(project?.project?.profileId ?? "");

      console.info("Profile options created:", _profileOptions);

      setProfileOptions([
        { label: i18n.t("cad-model-category-filter.profile.select-profile"), value: "", profileLevelType: ProfileLevelType.Application.toString() },
        ..._profileOptions,
      ]);
    } else {
      console.info("No profile data found, setting empty options");
      setProfileOptions([{ label: i18n.t("cad-model-category-filter.profile.select-profile"), value: "", profileLevelType: undefined }]);
      setSelectedProfileValue("");
      setActiveProfileId(null);
    }
  }, [profileData, project?.project?.profileId, projectId]);

  // Sync activeProfileId with selectedProfileValue
  useEffect(() => {
    setActiveProfileId(selectedProfileValue || null);
  }, [selectedProfileValue]);

  const handleImportComplete = async () => {
    console.log("Import completed, refetching profiles...");
    setIsImportMode(false);
    await refetchProfiles();
  };

  const handleImportCancel = () => {
    setIsImportMode(false);
    setImportFiles(undefined);
  };

  // Load metadata from project when project changes
  useEffect(() => {
    if (project?.project?.projectMetadata) {
      setMetadata(
        new ProjectMetadata({
          totalFloorArea: project.project.projectMetadata.totalFloorArea,
          serviceLife: project.project.projectMetadata.serviceLife,
          goal: project.project.projectMetadata.goal,
          scope: project.project.projectMetadata.scope,
        }),
      );
    }
    setHasMetadataChanged(false);
  }, [project]);

  const profileDetails = useProfileDetailsById(selectedProfileValue || "", {
    enabled: !!selectedProfileValue, // Only fetch when we have a selected profile ID
  });
  const handleMetadataChange = (name: string, value: any) => {
    setMetadata((prev) => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        goal: prev.goal,
        scope: prev.scope,
        [name]: value,
      };
      return new ProjectMetadata(newData);
    });
    setHasMetadataChanged(true);
  };

  const handleSaveMetadata = async () => {
    try {
      const projectMetadata = new ProjectMetadata({
        totalFloorArea: metadata.totalFloorArea,
        serviceLife: metadata.serviceLife,
        goal: metadata.goal,
        scope: metadata.scope,
      });

      await updateProjectMetadata.mutateAsync(projectMetadata);
      setHasMetadataChanged(false);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error("Error updating project metadata:", error);
      setErrorMessage("Failed to update project metadata. Please try again.");
      setShowErrorMessage(true);
    }
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const handleCloseProfileSuccessMessage = () => {
    setshowProfileSuccessMessage(false);
  };

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };
  const createProfileResponseModel = (data: Partial<ProfileResponse> = {}): ProfileResponseModel => {
    // console.info("createProfileResponseModel input data:", data);
    // console.info("Profile configuration response:", data.profileConfigurationResponse);
    const result = {
      srNo: 1,
      //id: data.,
      profileId: data.profileId,
      name: data.name,
      description: data.description,
      deleteStatus: "inactive",
      isDeleted: false,
      //rebarEnabled: data.project?.rebarEstimationStatus ?? false,
      profileConfigurationResponse: data.profileConfigurationResponse,
    };
    return result;
  };
  const handleProfileChange = (event: SelectChangeEvent) => {
    const newProfileId = event.target.value;
    console.info("Profile changed to:", newProfileId);
    setSelectedProfileValue(newProfileId);
    setActiveProfileId(newProfileId);
    // Check if profile has changed from the original
    setHasProfileChanged(newProfileId !== (project?.project?.profileId ?? ""));
    // The profileDetails query will automatically refetch when selectedProfileValue changes
  };

  // Add debugging for profile details
  useEffect(() => {
    console.info("Profile details changed:", {
      selectedProfileValue,
      activeProfileId,
      isLoading: profileDetails.isLoading,
      isError: profileDetails.isError,
      data: profileDetails.data,
    });
  }, [selectedProfileValue, activeProfileId, profileDetails.isLoading, profileDetails.isError, profileDetails.data]);

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"xl"}
        open={open}
        onClose={onClose}
        aria-labelledby="project-settings-dialog-title"
        PaperProps={{
          sx: {
            height: "80%",
          },
        }}
      >
        <DialogTitle
          id="project-settings-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("project-page.project-settings-title")}
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            paddingLeft: 2,
            paddingRight: 2,
            paddingBottom: 0,
            gap: 2,
          }}
        >
          {showMetadata && (
            <Box>
              <ProjectMetadataForm metadata={metadata} onChange={handleMetadataChange} />
              {hasMetadataChanged && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button variant="contained" onClick={handleSaveMetadata} disabled={updateProjectMetadata.isPending}>
                    {updateProjectMetadata.isPending ? i18n.t("project-settings-page.save-metadata-button.on-saving") : i18n.t("project-settings-page.save-metadata-button.title")}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {(appSettings.data?.rebarEstimation || appSettings.data?.profileLevelRebarSettings) && project?.project?.profile && (
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", padding: "16px", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Headers */}
                  {/* <Box sx={{ display: "flex", alignItems: "center", paddingX: 2, paddingY: 1, backgroundColor: "rgba(0, 0, 0, 0.04)", borderRadius: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "300px" }}>
                      <span style={{ fontWeight: "bold", fontSize: "0.875rem" }}>Profile</span>
                      <span style={{ fontWeight: "bold", fontSize: "0.875rem" }}>Profile Level</span>
                    </Box>
                  </Box> */}

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Select value={selectedProfileValue} displayEmpty sx={{ minWidth: "300px", width: "auto" }} onChange={handleProfileChange}>
                      {profileOptions.map((profile) => (
                        <MenuItem key={profile.value} value={profile.value}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <span>{profile.label}</span>
                            <Box>
                              {(profile.profileLevelType === ProfileLevelType.Application || profile.profileLevelType === ProfileLevelType.ApplicationImport) && (
                                <Chip
                                  label="Application"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.75rem",

                                    height: "24px",
                                    justifyContent: "center",

                                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                                    borderColor: (theme) => theme.palette.custom?.borderColor,
                                  }}
                                />
                              )}

                              {profile.profileLevelType === ProfileLevelType.Project && (
                                <Chip
                                  label="Project"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.75rem",                                  
                                    height: "24px",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(25, 118, 210, 0.08)",                                                       
                                    border: "1px solid",                                   
                                    borderColor: (theme) => theme.palette.custom?.borderColor,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>

                    {hasProfileChanged && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveProfile}
                        disabled={updateProjectProfile.isPending}
                        startIcon={<SaveIcon />}
                        sx={{
                          minWidth: "100px",
                          height: "56px", // Match Select component height
                        }}
                      >
                        {updateProjectProfile.isPending ? "Saving..." : "Save"}
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      onClick={handleExportProfile}
                      disabled={!selectedProfileValue || exportProfile.isPending}
                      startIcon={exportProfile.isPending ? <CircularProgress size={20} /> : <DownloadIcon />}
                      sx={{
                        minWidth: "100px",
                        height: "56px", // Match Select component height
                      }}
                    >
                      {exportProfile.isPending ? i18n.t("profile-tab.grid.export-button.processing") : i18n.t("profile-tab.grid.export-button.label")}
                    </Button>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  sx={{
                    minWidth: "100px",
                    height: "56px", // Match Select component height
                  }}
                  startIcon={<UploadFile />}
                  onClick={handleFileSelect}
                >
                  {i18n.t("project-page.project-settings.import-excel")}
                </Button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  style={{ display: "none" }}
                />
              </Box>

              {isImportMode ? (
                <Box sx={{ padding: "16px" }}>
                  <ProfileImportDisplay
                    files={importFiles}
                    onImportComplete={handleImportComplete}
                    onCancel={handleImportCancel}
                    profileLevelType={ProfileLevelType.Project}
                    parentId={projectId}
                  />
                </Box>
              ) : (
                <div>
                  {profileDetails.isLoading && activeProfileId ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>Loading profile details...</Box>
                  ) : profileDetails.isError && activeProfileId ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
                      <Alert severity="error">Failed to load profile details. Please try again.</Alert>
                    </Box>
                  ) : (
                    <Box sx={{ padding: "16px" }}>
                      <ProfileDetailPanel row={createProfileResponseModel(activeProfileId && profileDetails.data ? profileDetails.data : project?.profileResponse)} />
                    </Box>
                  )}
                </div>
              )}
              <RebarEstimationPage
                mode={materialMode}
                projectId={projectId}
                profileId={selectionManager.selectedProject?.profileId}
                originScreen="Project"
              />
              { appStore.hasFeature(FeatureManagementType.MaterialManagement) && (
                <MaterialsPage
                  key={`materials-${projectId}`}
                  mode={materialMode}
                  projectId={projectId}
                  profileId={selectionManager.selectedProject?.profileId}
                  originScreen="Project"
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Success message */}
      <Snackbar open={showSuccessMessage} autoHideDuration={3000} onClose={handleCloseSuccessMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseSuccessMessage} severity="success" variant="filled">
          Project metadata updated successfully!
        </Alert>
      </Snackbar>

      {/* Success message */}
      <Snackbar open={showProfileSuccessMessage} autoHideDuration={3000} onClose={handleCloseProfileSuccessMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseProfileSuccessMessage} severity="success" variant="filled">
          {i18n.t("project-page.project-settings.save-success")}
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar open={showErrorMessage} autoHideDuration={5000} onClose={handleCloseErrorMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseErrorMessage} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Export result message */}
      <Snackbar
        open={exportToast.open}
        autoHideDuration={3000}
        onClose={() => setExportToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setExportToast((prev) => ({ ...prev, open: false }))}
          severity={exportToast.success ? "success" : "error"}
          variant="filled"
        >
          {exportToast.success ? i18n.t("profile-tab.notification.export-success") : i18n.t("profile-tab.notification.export-error")}
        </Alert>
      </Snackbar>
    </>
  );
};
